import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Compass, Utensils, BedDouble, Briefcase } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { Button } from "./ui/button";

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const items = [
    { to: "/dashboard", label: t("sidebar_dashboard"), icon: LayoutDashboard, testid: "sidebar-dashboard" },
    { to: "/dashboard/tours", label: t("sidebar_tours"), icon: Compass, testid: "sidebar-tours" },
    { to: "/dashboard/restaurants", label: t("sidebar_restaurants"), icon: Utensils, testid: "sidebar-restaurants" },
    { to: "/dashboard/hotels", label: t("sidebar_hotels"), icon: BedDouble, testid: "sidebar-hotels" },
    { to: "/dashboard/services", label: t("sidebar_services"), icon: Briefcase, testid: "sidebar-services" },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-slate-200 bg-white">
        <Link to="/dashboard" className="flex items-center gap-2 px-6 py-6" data-testid="brand-link">
          <div className="w-9 h-9 rounded-lg bg-[#0A2540] grid place-items-center text-white font-bold font-display">R</div>
          <span className="font-display font-semibold text-lg">ReviewFlow</span>
        </Link>
        <nav className="flex-1 px-3 space-y-1">
          {items.map((it) => {
            const active = location.pathname === it.to || (it.to !== "/dashboard" && location.pathname.startsWith(it.to));
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  active ? "bg-[#0A2540] text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <it.icon className="w-4 h-4" /> {it.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-slate-500">{t("sidebar_connected_as")}</div>
            <div className="text-sm font-semibold text-slate-800 truncate">{user?.business_name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={() => {
              logout();
              navigate("/");
            }}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> {t("sidebar_logout")}
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b bg-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0A2540] grid place-items-center text-white font-bold font-display">R</div>
            <span className="font-display font-semibold">ReviewFlow</span>
          </div>
          <div className="flex gap-2 items-center">
            <Link to="/dashboard/tours" className="text-sm font-medium text-slate-700" data-testid="mobile-tours-link">{t("sidebar_tours")}</Link>
            <button onClick={() => { logout(); navigate("/"); }} className="text-sm font-medium text-slate-700" data-testid="mobile-logout-btn">{t("sidebar_logout")}</button>
          </div>
        </div>
        <div className="p-6 lg:p-10 max-w-7xl">
          <div className="flex justify-end mb-6">
            <LanguageSwitcher />
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
